import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { willTokenExpireSoon, getTokenTimeToExpiry } from '@/utils/jwt'

/**
 * Hook personnalisé pour gérer le rafraîchissement automatique du token JWT
 * Ce hook vérifie périodiquement si le token va expirer et le rafraîchit automatiquement
 * 
 * @param checkInterval Intervalle de vérification en millisecondes (par défaut 60 secondes)
 * @param refreshBeforeExpiry Nombre de secondes avant l'expiration pour rafraîchir (par défaut 5 minutes)
 */
export function useTokenRefresh(
  checkInterval: number = 60000, // Vérifier toutes les 60 secondes
  refreshBeforeExpiry: number = 300 // Rafraîchir 5 minutes avant l'expiration
) {
  const { token, isAuthenticated, refreshAuth } = useAuthStore()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isRefreshingRef = useRef(false)

  useEffect(() => {
    // Ne pas démarrer si non authentifié ou pas de token
    if (!isAuthenticated || !token) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    const checkAndRefreshToken = async () => {
      // Éviter les appels simultanés
      if (isRefreshingRef.current) {
        return
      }

      if (!token) {
        return
      }

      try {
        // Vérifier si le token va expirer bientôt
        if (willTokenExpireSoon(token, refreshBeforeExpiry)) {
          const timeToExpiry = getTokenTimeToExpiry(token)
          console.log(
            `Token va expirer dans ${timeToExpiry} secondes, rafraîchissement automatique...`
          )

          isRefreshingRef.current = true
          await refreshAuth()
          isRefreshingRef.current = false

          console.log('Token rafraîchi avec succès')
        }
      } catch (error) {
        console.error('Erreur lors du rafraîchissement automatique du token:', error)
        isRefreshingRef.current = false
      }
    }

    // Vérifier immédiatement au montage
    checkAndRefreshToken()

    // Puis vérifier périodiquement
    intervalRef.current = setInterval(checkAndRefreshToken, checkInterval)

    // Nettoyage
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [token, isAuthenticated, refreshAuth, checkInterval, refreshBeforeExpiry])
}

